import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ShippingInfo from '@components/productDetail/ShippingInfo';
import BackToSearchLink from '@components/productDetail/BackToSearchLink';

import { CompatibilityContext } from '../../../contexts/compatibility';

import * as settingRoutes from '@src/app_routes/settings';
import * as diamondRoutes from '@src/app_routes/diamonds';
import * as dyorRoutes from '@src/app_routes/ringBuilder';

import useProductDetailColumnCount from '@hooks/useProductDetailColumnCount';

import css from '@styles/productDetail';

import type { TSettingDetail } from '@types/setting';

function Actions({ setting }: TSettingDetail) {
  const currentDiamondId = useSelector(state => state.ringBuilder.diamond.id);
  const { compatibility } = useContext(CompatibilityContext);
  const columnCount = useProductDetailColumnCount();

  const dispatch = useDispatch();
  const handleChooseClick = useCallback(() => {
    if (!setting) return;

    dispatch({
      type: 'SELECT_DYOR_SETTING_SKU_AND_DIAMOND_ID',
      // If a setting sku and diamond id are both present, recommended might
      // be present, but lastCheckedSettingSku definitely will.
      //
      // If both setting sku and diamond id were not present during
      // compatibility check, neither recommended nor
      // lastCheckedSettingSku will be present. In that case, we need to add
      // the sku of whatever setting is displayed on the page at the moment.
      payload: {
        settingSku:
          compatibility.recommended ||
          compatibility.lastCheckedSettingSku ||
          setting.vendor_sku,
        diamondId: currentDiamondId
      },
      onSuccess: !currentDiamondId
        ? () => {
            dispatch({
              type: 'RESET_DIAMOND_FILTERS',
              filters: {
                shape: [
                  'Heart-Shaped',
                  'Round',
                  'Princess-Cut',
                  'Oval',
                  'Marquise',
                  'Pear-Shaped',
                  'Cushion-Cut',
                  'Emerald-Cut',
                  'Asscher'
                ]
              }
            });
          }
        : undefined,
      redirectTo:
        (currentDiamondId &&
          dyorRoutes.combinedStep({
            diamond: currentDiamondId,
            setting: setting.vendor_sku
          })) ||
        diamondRoutes.searchPath()
    });
  }, [
    dispatch,
    setting,
    compatibility.recommended,
    compatibility.lastCheckedSettingSku,
    currentDiamondId
  ]);

  if (!setting) return null;

  let actions = [{ name: 'addToCompare', title: 'To Save & Compare' }];

  if (compatibility.compatible || compatibility.recommended) {
    actions.push({
      name: 'addToDyor',
      title: `To ${currentDiamondId ? 'your' : 'a'} Diamond`
    });
  }

  return (
    <div className={css.actionsContainer}>
      <button className={css.chooseButton} onClick={handleChooseClick}>
        Choose this setting
      </button>
      <BackToSearchLink url={settingRoutes.searchPath()} />
    </div>
  );
}

export default function ShippingAndActions({
  setting
}: {
  setting: TSettingDetail
}) {
  const columnCount = useProductDetailColumnCount();

  if (!setting) return null;

  return (
    <div className={css.shippingAndActions}>
      {columnCount > 1 ? (
        <ShippingInfo deliveryDate={setting.delivery_date} borders={false} />
      ) : null}
      <Actions setting={setting} />
    </div>
  );
}
